
import type { QueryRanking, QueryRankingToolConfigProvider } from '@empathyco/playboard-core-domain'
import type { SetupContext } from 'vue'
import type { Route } from 'vue-router'
import type { IToolingListComposableProps } from '../../composables/use-tooling-list'
import type {
  DataTableColumn,
  DataTableOption,
  GuardResolveFunction,
  ToolsMessages,
} from '../../models'
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'
import { computed, defineComponent, getCurrentInstance, ref, watch } from 'vue'
import ConfigurationFeaturesStatus from '../../components/configurations/configuration-features-status.component.vue'
import CrudList from '../../components/crud/crud-list.component.vue'
import DataTable from '../../components/crud/data-table.component.vue'
import EpbPill from '../../components/shared/epb-pill/epb-pill.component.vue'
import { useBrowseListFilter, useEndpoints } from '../../composables'
import { useToolingList } from '../../composables/use-tooling-list'
import { useToolingRestricted } from '../../composables/use-tooling-restricted'
import { CONTAINER } from '../../config/inversify.config'
import { TYPES } from '../../config/inversify.types'
import { AlignEnum, ConfigurationToolIdEnum, FeatureIdEnum, SortDirectionEnum } from '../../enums'
import { ROUTER_CONSTS } from '../../router/router.consts'

export default defineComponent({
  name: 'SearchQueryRankingList',
  components: {
    ConfigurationFeaturesStatus,
    CrudList,
    DataTable,
    EpbPill,
  },
  async beforeRouteLeave(to: Route, from: Route, next: GuardResolveFunction) {
    this.contextWatchDisabled = true

    next()
  },
  setup(props: IToolingListComposableProps, ctx: SetupContext) {
    const {
      proxy: { $epb },
    } = getCurrentInstance()

    const baseToolsIcon = [
      ConfigurationToolIdEnum.ATTRIBUTES_RANKING,
      ConfigurationToolIdEnum.PRODUCTS_RANKING,
      ConfigurationToolIdEnum.PINNED_PRODUCTS,
      ConfigurationToolIdEnum.HIDDEN_ATTRIBUTES,
      ConfigurationToolIdEnum.HIDDEN_PRODUCTS,
    ]

    const messages: ToolsMessages['configurations'] = $epb.messages.tools.configurations

    const routes = ROUTER_CONSTS.routes[FeatureIdEnum.CONFIGURATION].search

    const toolConfig = CONTAINER.get<QueryRankingToolConfigProvider>(
      TYPES.QueryRankingToolConfigProvider,
    )()

    const { endpoints } = useEndpoints()

    const { browseExtraParams } = useBrowseListFilter()

    const loadDataExtraParams = computed(() => ({
      endpointId: endpoints.value.map(endpoint => endpoint.value),
      ...browseExtraParams.value,
    }))

    const toolingList = useToolingList<QueryRanking>(props, ctx, {
      routes,
      messages,
      defaultSorting: {
        field: 'updated',
        direction: SortDirectionEnum.DESCENDING,
      },
      loadDataExtraParams,
    })

    watch(
      [endpoints, browseExtraParams],
      ([loadedEndpoints]) => {
        if (loadedEndpoints.length > 0) {
          toolingList.loadData()
        }
      },
      { immediate: true },
    )

    const { isRestricted } = useToolingRestricted()

    const columns: Array<DataTableColumn> = [
      {
        id: 'title',
        text: messages.fields.name.label,
        sortable: true,
      },
      ...(endpoints.value.length > 1
        ? [
            {
              id: 'endpointId',
              text: messages.fields.endpointId.label,
            },
          ]
        : []),
      {
        id: 'criteria.queries',
        text: !toolConfig.browseFields?.length
          ? messages.fields.keywords.label
          : messages.fields.queriesAttribute.label,
        unwrapText: true,
        copyToClipboard: true,
      },
      ...toolingList.visiblePivotColumns.value,
      {
        id: 'modified',
        text: messages.fields.features.label,
        width: '12rem',
        align: AlignEnum.CENTER,
      },
      {
        id: 'metadata.updated',
        text: messages.fields.updateDate.label,
        sortable: true,
        sortableId: 'updated',
      },
      {
        id: 'metadata.author',
        text: messages.fields.user.label,
      },
      {
        id: 'criteria.enabled',
        text: messages.fields.enabled.label,
      },
      {
        id: 'options',
        width: '44px',
        align: AlignEnum.CENTER,
      },
    ]

    const readOnlyDataItemIds = computed(() => {
      return toolingList.data.value.results
        .filter(item => isRestricted(item.restrictedRoles))
        .map(item => item.id)
    })

    function onCheckboxClick(dataItem: QueryRanking): void {
      if (isRestricted(dataItem.restrictedRoles)) {
        toolingList.navigateToRoute(routes.update, dataItem)
      } else {
        toolingList.onCheckboxClick(dataItem)
      }
    }

    function onRowClick(dataItem: QueryRanking): void {
      toolingList.navigateToRoute(routes.update, dataItem)
    }

    const crudListRef = ref(null)

    const onOptionsClick = (option: DataTableOption, dataItem): void => {
      toolingList.onOptionClick(option, dataItem, crudListRef)
    }

    function getBrowseValueText(dataItem: QueryRanking): string | undefined {
      if (dataItem.criteria.browseValue) {
        return dataItem.criteria.browseValue.replace('__', ' > ')
      }
    }

    return {
      ...toolingList,
      DomainEnum,
      baseClass: 'epb-search-query-ranking-list',
      readOnlyDataItemIds,
      columns,
      messages,
      baseToolsIcon,
      routes,
      onCheckboxClick,
      onRowClick,
      crudListRef,
      onOptionsClick,
      getBrowseValueText,
    }
  },
})
